import { useState } from 'react';
import {
  TagSummary,
  useCreateTag,
  useDeleteTag,
  useRenameTag,
  useTags,
} from '../api';

export default function TagsAdmin() {
  const { data, isLoading, error } = useTags();
  const [creating, setCreating] = useState(false);

  return (
    <div className="mx-auto max-w-3xl space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold">Tags</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Renaming or deleting a tag updates every household and person carrying it.
          </p>
        </div>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            + New tag
          </button>
        )}
      </header>

      {creating && <NewTagRow onDone={() => setCreating(false)} />}

      {isLoading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {error && <p className="text-sm text-rose-600 dark:text-rose-400">Error loading tags.</p>}
      {data && data.tags.length === 0 && !creating && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No tags yet. Create one here, or add a tag to a household or person.
        </p>
      )}
      {data && data.tags.length > 0 && (
        <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                <th className="px-3 py-1">Tag</th>
                <th className="px-3 py-1 text-right">Households</th>
                <th className="px-3 py-1 text-right">People</th>
                <th className="px-3 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {data.tags.map((t) => (
                <TagRow key={t.name} tag={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewTagRow({ onDone }: { onDone: () => void }) {
  const create = useCreateTag();
  const [name, setName] = useState('');

  const submit = () => {
    const n = name.trim();
    if (!n) return;
    create.mutate(n, { onSuccess: () => { setName(''); onDone(); } });
  };

  return (
    <div className="space-y-1 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2">
      <div className="flex items-center gap-1">
        <input
          className="w-48 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-sm"
          type="text"
          value={name}
          placeholder='e.g. "needs visit"'
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') onDone();
          }}
          autoFocus
        />
        <button
          className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
          onClick={submit}
          disabled={create.isPending || !name.trim()}
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <button
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
          onClick={onDone}
          disabled={create.isPending}
        >
          Cancel
        </button>
      </div>
      {create.error && (
        <p className="text-xs text-rose-700 dark:text-rose-300">
          Create failed: {(create.error as Error).message}
        </p>
      )}
    </div>
  );
}

function TagRow({ tag: t }: { tag: TagSummary }) {
  const rename = useRenameTag();
  const del = useDeleteTag();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(t.name);

  const saveRename = () => {
    const next = draft.trim();
    if (!next || next === t.name) { setEditing(false); return; }
    rename.mutate({ name: t.name, new_name: next }, { onSuccess: () => setEditing(false) });
  };

  const remove = () => {
    const usage = t.household_count + t.people_count;
    const warning = usage > 0
      ? `Delete tag "${t.name}"? It will be removed from ${t.household_count} household(s) and ${t.people_count} person(s).`
      : `Delete tag "${t.name}"?`;
    if (!confirm(warning)) return;
    del.mutate(t.name);
  };

  return (
    <tr className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
      <td className="px-3 py-1.5">
        {editing ? (
          <span className="inline-flex items-center gap-1">
            <input
              className="w-48 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-sm"
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveRename();
                if (e.key === 'Escape') { setDraft(t.name); setEditing(false); }
              }}
              autoFocus
            />
            <button
              className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
              onClick={saveRename}
              disabled={rename.isPending || !draft.trim()}
            >
              {rename.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
              onClick={() => { setDraft(t.name); setEditing(false); }}
              disabled={rename.isPending}
            >
              Cancel
            </button>
          </span>
        ) : (
          <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs text-slate-700 dark:text-slate-300">
            {t.name}
          </span>
        )}
        {(rename.error || del.error) && (
          <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-300">
            {((rename.error || del.error) as Error).message}
          </p>
        )}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600 dark:text-slate-400">
        {t.household_count}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600 dark:text-slate-400">
        {t.people_count}
      </td>
      <td className="px-3 py-1.5 text-right">
        {!editing && (
          <span className="flex justify-end gap-2 text-xs text-slate-400 dark:text-slate-500">
            <button
              className="hover:text-slate-700 dark:hover:text-slate-300"
              onClick={() => { setDraft(t.name); setEditing(true); }}
            >
              Rename
            </button>
            <button
              className="hover:text-red-600 dark:hover:text-red-400"
              onClick={remove}
              disabled={del.isPending}
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}
